// Steps 1 → 3 state for the submission flow: sign-up form, email verification
// (where the account is created), materials, and phone verification.

import { createModel } from "@rematch/core";
import {
  getSubmissionFlowApi,
  IAccountDefaultsDto,
  IMediaAssetDto,
  ISendPhoneCodeRequest,
  IStartSignupRequest,
  ISubmissionDraftDto,
} from "../shared/api/submission";
import {
  clearSubmissionAuth,
  getSubmissionAuth,
  setSubmissionAuth,
} from "../helpers/submissionAuth";

import { flowRefs } from "../components/Submission/common/submissionParams";
import { forgetBreakdownInfoOn } from "../components/Submission/common/breakdownInfo";
import { trackSubmissionEvent } from "../components/Submission/analytics";

const SESSION_KEY = "sfSessionId.v1";

export type VerifyStatus = "idle" | "verifying" | "error" | "locked" | "verified";

export interface ISubmissionSignupState {
  sessionId: string | null;
  email: string;
  maskedEmail: string;
  // absolute epoch ms — derived from the server, so a refresh or a
  // backgrounded tab resumes the countdown accurately
  resendAvailableAt: number;
  attemptsRemaining: number;
  verifyStatus: VerifyStatus;
  errorCode: string | null;
  errorMessage: string | null;
  loading: boolean;

  clientId: number | null;
  accountDefaults: IAccountDefaultsDto | null;

  draft: ISubmissionDraftDto | null;
  photo: IMediaAssetDto | null;
  resume: IMediaAssetDto | null;
  note: string;
  ageAttested: boolean;

  phone: string;
  maskedPhone: string;
  phoneResendAvailableAt: number;
  phoneAttemptsRemaining: number;
  phoneStatus: VerifyStatus;
  phoneCodeSent: boolean;
}

const initialState: ISubmissionSignupState = {
  sessionId: null,
  email: "",
  maskedEmail: "",
  resendAvailableAt: 0,
  attemptsRemaining: 4,
  verifyStatus: "idle",
  errorCode: null,
  errorMessage: null,
  loading: false,

  clientId: null,
  accountDefaults: null,

  draft: null,
  photo: null,
  resume: null,
  note: "",
  ageAttested: false,

  phone: "",
  maskedPhone: "",
  phoneResendAvailableAt: 0,
  phoneAttemptsRemaining: 4,
  phoneStatus: "idle",
  phoneCodeSent: false,
};

const readSessionId = (): string | null => {
  try {
    return window.sessionStorage.getItem(SESSION_KEY);
  } catch (e) {
    return null;
  }
};

const writeSessionId = (sessionId: string) => {
  try {
    window.sessionStorage.setItem(SESSION_KEY, sessionId);
  } catch (e) {
    // ignore
  }
};

// The signup started in this tab (its email code's session), the sign-in kept: a sign-in as an
// existing account replaces it (common/tabAccount.ts).
export const forgetSignupSession = () => {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch (e) {
    // ignore
  }
};

export const clearSubmissionSession = () => {
  forgetSignupSession();
  clearSubmissionAuth();
};

// Server-provided cooldown wins over any local guess (clock skew, refresh).
const cooldownToEpoch = (seconds: number) => Date.now() + seconds * 1000;

const errorOf = (err: any): { code: string; message: string } => {
  if (err && err.isNetworkError) {
    return { code: "network", message: err.message };
  }
  if (err && err.errorCode) {
    // A breakdown that closed (or a role cast) meanwhile: the kept info is out of date.
    forgetBreakdownInfoOn(err.errorCode);
    return { code: err.errorCode, message: err.message || "" };
  }
  return { code: "unknown", message: (err && err.message) || "" };
};

const submissionSignupModel = createModel({
  state: initialState,
  reducers: {
    setLoading(state: ISubmissionSignupState, loading: boolean) {
      return { ...state, loading };
    },
    setError(state: ISubmissionSignupState, payload: { code: string; message: string }) {
      return {
        ...state,
        errorCode: payload.code,
        errorMessage: payload.message,
        loading: false,
      };
    },
    clearError(state: ISubmissionSignupState) {
      return { ...state, errorCode: null, errorMessage: null };
    },
    setSession(
      state: ISubmissionSignupState,
      payload: {
        sessionId: string;
        email: string;
        maskedEmail: string;
        resendAvailableAt: number;
      }
    ) {
      return {
        ...state,
        ...payload,
        verifyStatus: "idle" as VerifyStatus,
        attemptsRemaining: 4,
        errorCode: null,
        errorMessage: null,
        loading: false,
      };
    },
    setVerifyStatus(state: ISubmissionSignupState, verifyStatus: VerifyStatus) {
      return { ...state, verifyStatus };
    },
    setAttemptsRemaining(state: ISubmissionSignupState, attemptsRemaining: number) {
      return { ...state, attemptsRemaining };
    },
    setResendAvailableAt(state: ISubmissionSignupState, resendAvailableAt: number) {
      return { ...state, resendAvailableAt };
    },
    setAccount(
      state: ISubmissionSignupState,
      // null when the actor signed in through the classic login (no signup defaults).
      payload: { clientId: number; accountDefaults: IAccountDefaultsDto | null }
    ) {
      return {
        ...state,
        clientId: payload.clientId,
        accountDefaults: payload.accountDefaults,
        verifyStatus: "verified" as VerifyStatus,
        loading: false,
        errorCode: null,
        errorMessage: null,
      };
    },
    setDraft(state: ISubmissionSignupState, draft: ISubmissionDraftDto) {
      // The draft is the server's copy of step 2. After a reload the page's own
      // fields are empty while the draft still holds the photo, note and
      // attestation — so step 2 showed a blank dropzone for a submission that
      // had a photo, and "Edit" from the review screen looked like it had lost
      // everything. Each field fills from the draft only when it is empty, so a
      // change the visitor is making right now is never overwritten.
      return {
        ...state,
        draft,
        photo: state.photo || draft.photo || null,
        resume: state.resume || draft.resume || null,
        note: state.note || draft.noteToCastingDirector || "",
        ageAttested: state.ageAttested || !!draft.ageAttested,
        loading: false,
      };
    },
    setPhoto(state: ISubmissionSignupState, photo: IMediaAssetDto | null) {
      return { ...state, photo, loading: false };
    },
    setResume(state: ISubmissionSignupState, resume: IMediaAssetDto | null) {
      return { ...state, resume, loading: false };
    },
    setNote(state: ISubmissionSignupState, note: string) {
      return { ...state, note };
    },
    setAgeAttested(state: ISubmissionSignupState, ageAttested: boolean) {
      return { ...state, ageAttested };
    },
    setPhone(state: ISubmissionSignupState, phone: string) {
      return { ...state, phone };
    },
    setPhoneSent(
      state: ISubmissionSignupState,
      payload: { maskedPhone: string; resendAvailableAt: number }
    ) {
      return {
        ...state,
        maskedPhone: payload.maskedPhone,
        phoneResendAvailableAt: payload.resendAvailableAt,
        phoneCodeSent: true,
        phoneStatus: "idle" as VerifyStatus,
        phoneAttemptsRemaining: 4,
        loading: false,
        errorCode: null,
        errorMessage: null,
      };
    },
    setPhoneStatus(state: ISubmissionSignupState, phoneStatus: VerifyStatus) {
      return { ...state, phoneStatus };
    },
    // Cooldown-only update. Must NOT reuse setPhoneSent: that reducer records a
    // successful send (clears the error, resets the attempt budget, wipes
    // maskedPhone), which would fake a send the server explicitly refused.
    setPhoneResendAvailableAt(state: ISubmissionSignupState, phoneResendAvailableAt: number) {
      return { ...state, phoneResendAvailableAt, loading: false };
    },
    setPhoneAttemptsRemaining(state: ISubmissionSignupState, phoneAttemptsRemaining: number) {
      return { ...state, phoneAttemptsRemaining };
    },
    restoreState(state: ISubmissionSignupState, payload: Partial<ISubmissionSignupState>) {
      return { ...state, ...payload };
    },
    reset() {
      return { ...initialState };
    },
  },
  effects: (dispatch: any) => ({
    // --- Step 1 -----------------------------------------------------------

    async startSignup(payload: IStartSignupRequest) {
      dispatch.submissionSignupModel.setLoading(true);
      dispatch.submissionSignupModel.clearError();
      try {
        const resp = await getSubmissionFlowApi().startSignup(payload);
        writeSessionId(resp.sessionId);
        dispatch.submissionSignupModel.setSession({
          sessionId: resp.sessionId,
          email: payload.email,
          maskedEmail: resp.maskedEmail,
          resendAvailableAt: cooldownToEpoch(resp.resendCooldownSeconds),
        });
        return resp;
      } catch (err) {
        dispatch.submissionSignupModel.setError(errorOf(err));
        throw err;
      }
    },

    // --- Step 1b: verification — creates the account ----------------------

    async verifyEmailCode(payload: { code: string }, rootState: any) {
      const state: ISubmissionSignupState = rootState.submissionSignupModel;
      const sessionId = state.sessionId || readSessionId();
      if (!sessionId) {
        dispatch.submissionSignupModel.setError({
          code: "sessionNotFound",
          message: "",
        });
        return null;
      }
      if (state.verifyStatus === "verifying") return null;

      dispatch.submissionSignupModel.setVerifyStatus("verifying");
      dispatch.submissionSignupModel.clearError();
      try {
        const resp = await getSubmissionFlowApi().verifyEmailCode(sessionId, payload.code);
        setSubmissionAuth({
          clientId: resp.clientId,
          jwt: resp.jwt,
          refreshToken: resp.refreshToken,
          expiresIn: resp.expiresIn,
        });
        dispatch.submissionSignupModel.setAccount({
          clientId: resp.clientId,
          accountDefaults: resp.accountDefaults,
        });
        return resp;
      } catch (err) {
        const e = errorOf(err);
        const anyErr = err as any;
        // The account exists but token issuance failed — do NOT tell the user
        // verification failed; the page routes them to log in instead.
        if (e.code === "authTokenFailed" && anyErr && anyErr.clientId) {
          dispatch.submissionSignupModel.setError(e);
          dispatch.submissionSignupModel.setVerifyStatus("error");
          return null;
        }
        if (e.code === "tooManyAttempts") {
          dispatch.submissionSignupModel.setVerifyStatus("locked");
        } else {
          dispatch.submissionSignupModel.setVerifyStatus("error");
        }
        if (anyErr && typeof anyErr.attemptsRemaining === "number") {
          dispatch.submissionSignupModel.setAttemptsRemaining(anyErr.attemptsRemaining);
        } else if (e.code === "invalidCode") {
          dispatch.submissionSignupModel.setAttemptsRemaining(
            Math.max(0, state.attemptsRemaining - 1)
          );
        }
        dispatch.submissionSignupModel.setError(e);
        return null;
      }
    },

    async resendEmailCode(_: void, rootState: any) {
      const state: ISubmissionSignupState = rootState.submissionSignupModel;
      const sessionId = state.sessionId || readSessionId();
      if (!sessionId) return null;
      dispatch.submissionSignupModel.setLoading(true);
      try {
        const resp = await getSubmissionFlowApi().resendEmailCode(sessionId);
        dispatch.submissionSignupModel.setResendAvailableAt(
          cooldownToEpoch(resp.resendCooldownSeconds)
        );
        dispatch.submissionSignupModel.setAttemptsRemaining(4);
        dispatch.submissionSignupModel.setVerifyStatus("idle");
        dispatch.submissionSignupModel.setLoading(false);
        dispatch.submissionSignupModel.clearError();
        return resp;
      } catch (err) {
        const e = errorOf(err);
        const retryAfter = (err as any) && (err as any).retryAfterSeconds;
        if (e.code === "resendCooldown" && retryAfter) {
          // Server clock wins — restart the countdown silently.
          dispatch.submissionSignupModel.setResendAvailableAt(cooldownToEpoch(retryAfter));
          dispatch.submissionSignupModel.setLoading(false);
          return null;
        }
        dispatch.submissionSignupModel.setError(e);
        return null;
      }
    },

    // Restores the verification page after a refresh or a cold deep link.
    // Returns a discriminated result so the page can tell "this session is
    // genuinely gone" (show the start-again screen) from "the network blipped"
    // (offer a retry) — collapsing both into null told users with a valid
    // 90-minute code that their link had expired.
    async loadSignupState() {
      const sessionId = readSessionId();
      if (!sessionId) return { ok: false, reason: "missing" as const };
      try {
        const resp = await getSubmissionFlowApi().getSignupState(sessionId);
        const auth = getSubmissionAuth();
        dispatch.submissionSignupModel.restoreState({
          sessionId: resp.sessionId,
          maskedEmail: resp.maskedEmail,
          attemptsRemaining: resp.attemptsRemaining,
          resendAvailableAt: Date.parse(resp.resendAvailableAtUtc) || 0,
          verifyStatus: resp.verified ? "verified" : "idle",
          clientId: auth ? auth.clientId : null,
        });
        return { ok: true as const, state: resp };
      } catch (err) {
        const e = errorOf(err);
        dispatch.submissionSignupModel.setError(e);
        const gone =
          e.code === "sessionNotFound" || ((err as any) && (err as any).status === 404);
        return { ok: false as const, reason: gone ? ("gone" as const) : ("transient" as const) };
      }
    },

    // --- Step 2: materials ------------------------------------------------

    async ensureDraft(payload: { breakdownId: string | null; roleId: string | null }) {
      // The link wins (flowRefs): a step's first render, right after the login, has an
      // empty store, and a draft asked for with nothing is one with no breakdown.
      const refs = flowRefs(payload, typeof window !== "undefined" ? window.location.search : "");
      try {
        const draft = await getSubmissionFlowApi().createDraft(refs.breakdownId, refs.roleId);
        dispatch.submissionSignupModel.setDraft(draft);
        return draft;
      } catch (err) {
        dispatch.submissionSignupModel.setError(errorOf(err));
        return null;
      }
    },

    async uploadFile(payload: { kind: "photo" | "resume"; file: File }) {
      dispatch.submissionSignupModel.setLoading(true);
      dispatch.submissionSignupModel.clearError();
      try {
        const asset = await getSubmissionFlowApi().uploadMedia({
          kind: payload.kind,
          fileName: payload.file.name,
          sizeBytes: payload.file.size,
          mimeType: payload.file.type,
          file: payload.file,
        });
        if (payload.kind === "photo") dispatch.submissionSignupModel.setPhoto(asset);
        else dispatch.submissionSignupModel.setResume(asset);
        return asset;
      } catch (err) {
        dispatch.submissionSignupModel.setError(errorOf(err));
        return null;
      }
    },

    async saveMaterials(_: void, rootState: any) {
      const state: ISubmissionSignupState = rootState.submissionSignupModel;
      if (!state.draft) return null;
      dispatch.submissionSignupModel.setLoading(true);
      dispatch.submissionSignupModel.clearError();
      try {
        const draft = await getSubmissionFlowApi().saveMaterials(state.draft.submissionId, {
          photoAssetId: state.photo ? state.photo.assetId : undefined,
          resumeAssetId: state.resume ? state.resume.assetId : undefined,
          noteToCastingDirector: state.note,
          ageAttested: state.ageAttested,
        });
        dispatch.submissionSignupModel.setDraft(draft);
        return draft;
      } catch (err) {
        dispatch.submissionSignupModel.setError(errorOf(err));
        return null;
      }
    },

    // Step 2 left before it's done (docs/31): what the page has, as it is, and
    // the link back by email. The step stays open.
    async saveForLater(_: void, rootState: any) {
      const state: ISubmissionSignupState = rootState.submissionSignupModel;
      if (!state.draft) return null;
      dispatch.submissionSignupModel.setLoading(true);
      dispatch.submissionSignupModel.clearError();
      try {
        const result = await getSubmissionFlowApi().saveForLater(state.draft.submissionId, {
          photoAssetId: state.photo ? state.photo.assetId : undefined,
          resumeAssetId: state.resume ? state.resume.assetId : undefined,
          noteToCastingDirector: state.note,
          ageAttested: state.ageAttested,
          sendEmail: true,
        });
        dispatch.submissionSignupModel.setDraft(result.draft);
        return result;
      } catch (err) {
        dispatch.submissionSignupModel.setError(errorOf(err));
        return null;
      }
    },

    // --- Step 3: phone ----------------------------------------------------

    async sendPhoneCode(payload: ISendPhoneCodeRequest) {
      dispatch.submissionSignupModel.setLoading(true);
      dispatch.submissionSignupModel.clearError();
      try {
        const resp = await getSubmissionFlowApi().sendPhoneCode(payload);
        dispatch.submissionSignupModel.setPhone(payload.phone);
        dispatch.submissionSignupModel.setPhoneSent({
          maskedPhone: resp.maskedPhone,
          resendAvailableAt: cooldownToEpoch(resp.resendCooldownSeconds),
        });
        return resp;
      } catch (err) {
        const e = errorOf(err);
        const retryAfter = (err as any) && (err as any).retryAfterSeconds;
        if (e.code === "resendCooldown" && retryAfter) {
          // Server clock wins: restart the countdown, keep the existing
          // masked number and attempt budget, and still surface the reason so
          // the tap isn't silent.
          dispatch.submissionSignupModel.setPhoneResendAvailableAt(
            cooldownToEpoch(retryAfter)
          );
          dispatch.submissionSignupModel.setError(e);
          return null;
        }
        dispatch.submissionSignupModel.setError(e);
        return null;
      }
    },

    async verifyPhoneCode(payload: { code: string; recaptchaToken?: string; trackingData?: object }, rootState: any) {
      const state: ISubmissionSignupState = rootState.submissionSignupModel;
      if (state.phoneStatus === "verifying") return null;
      dispatch.submissionSignupModel.setPhoneStatus("verifying");
      dispatch.submissionSignupModel.clearError();
      try {
        const resp = await getSubmissionFlowApi().verifyPhoneCode(payload.code, {
          phone: state.phone,
          recaptchaToken: payload.recaptchaToken,
          trackingData: payload.trackingData,
        });
        dispatch.submissionSignupModel.setPhoneStatus("verified");
        return resp;
      } catch (err) {
        const e = errorOf(err);
        trackSubmissionEvent("sf_step_failed", { step: "phone_code", reason: e.code || "unknown" });
        // The actor phone API doesn't say how many tries are left; its 429 is the limit.
        dispatch.submissionSignupModel.setPhoneStatus(e.code === "tooManyAttempts" ? "locked" : "error");
        dispatch.submissionSignupModel.setError(e);
        return null;
      }
    },

    // A phone confirmed before (on the membership site, for an earlier submission) skips step 3.
    // When the check fails, the step simply asks for the phone.
    async loadPhoneStatus() {
      try {
        return await getSubmissionFlowApi().getPhoneStatus();
      } catch (err) {
        return null;
      }
    },

    // Step 3 done, on the submission: Registration checks the confirmation the phone API wrote.
    async recordPhone(_: void, rootState: any) {
      const state: ISubmissionSignupState = rootState.submissionSignupModel;
      if (!state.draft) {
        dispatch.submissionSignupModel.setError({ code: "submissionNotFound", message: "" });
        return null;
      }
      try {
        const draft = await getSubmissionFlowApi().recordPhone(state.draft.submissionId);
        dispatch.submissionSignupModel.setDraft(draft);
        return draft;
      } catch (err) {
        dispatch.submissionSignupModel.setError(errorOf(err));
        return null;
      }
    },

    async resetFlow() {
      clearSubmissionSession();
      dispatch.submissionSignupModel.reset();
    },
  }),
});

export default submissionSignupModel;
